
import { Language, NavItem, Testimonial, QuizQuestion, QuizResultType, QuizResult, BlogPost } from './types';
import { NAV_ITEMS, TESTIMONIALS, TESTIMONIALS_ES, QUIZ_QUESTIONS, QUIZ_RESULTS, RESOURCES, RESOURCES_ES } from './constants';

type ContentStructure = {
  nav: NavItem[];
  common: {
    bookPerformanceCheck: string;
    bookDemo: string;
    talkToAI: string;
    privacyNote: string;
    footerDesc: string;
    serving: string;
    partnership: string;
    resources: string;
    contact: string;
    scheduleCall: string;
    disclaimer: string;
    rights: string;
    links: {
      physicians: string;
      economics: string;
      onboarding: string;
      intake: string;
      realityCheck: string;
      blog: string;
      hedis: string;
      privacy: string;
    };
    footerBottom: {
      terms: string;
      privacy: string;
      security: string;
      cookies: string;
      doNotShare: string;
    }
  };
  home: {
    heroTitle: string;
    heroSubtitle: string;
    heroDesc: string;
    supporting: string;
    earningsConcept: string;
    economicsLabel: string;
    timeHorizons: {
      p1: { start: string; highlight: string; end: string };
      p2: { start: string; highlight1: string; mid: string; highlight2: string; end: string };
    };
    onboardingPromo: {
      title: string;
      desc: string;
      badge: string;
      note: string;
    };
    pillarsTitle: string;
    pillarsSubtitle: string;
    pillars: { title: string; desc: string; icon: string }[];
    twoLanesTitle: string;
    twoLanesDesc: string;
    lane1Title: string;
    lane1Desc: string;
    lane2Title: string;
    lane2Desc: string;
    compareLink: string;
    proofTitle: string;
    proofDesc: string;
    credibilityLocations: string;
    testimonialsTitle: string;
    timelineTitle: string;
    timelineSteps: { day: string; title: string; desc: string }[];
    timelineLink: string;
    whatChangesTitle: string;
    whatChangesItems: { title: string; desc: string }[];
    faq: {
      title: string;
      items: { question: string; answer: string }[];
    };
    ctaTitle: string;
    ctaDesc: string;
    ctaButton: string;
    contactButton: string;
  };
  earningsLadder: {
    title: string;
    steps: { title: string; desc: string; icon: string }[];
    note: string;
  };
  calculator: {
    title: string;
    labels: {
      patients: string;
      pool: string;
      surplus: string;
      share: string;
    };
    results: {
      totalSurplus: string;
      providerShare: string;
    };
    cta: string;
    disclaimer: string;
    accordionTitle: string;
    accordionContent: string;
  };
  whyJoin: {
    title: string;
    bullets: string[];
    faqTitle: string;
    faqs: { q: string; a: string }[];
  };
  curiousStrip: {
    title: string;
    emailBtn: string;
    callBtn: string;
    bookBtn: string;
    legal: string;
  };
  testimonials: Testimonial[];
  quiz: {
    questions: QuizQuestion[];
    results: Record<QuizResultType, QuizResult>;
    ui: {
      title: string;
      intro: string;
      startBtn: string;
      meta: string;
      questionCount: string;
      yourArchetype: string;
      painPoints: string;
      fix: string;
      bookCta: string;
      bookDesc: string;
      truthTableTitle: string;
      truthTableDesc: string;
      tableHeaders: string[]; 
      tableRows: {
        ffs: string[];
        shared: string[];
        suncoast: string[];
      }
    }
  };
  physicians: {
    title: string;
    subtitle: string;
    pcpTitle: string;
    pcpPoints: string[];
    adminTitle: string;
    adminPoints: string[];
    voicesSection: { title: string; subtitle: string };
  };
  economics: {
    title: string;
    subtitle: string;
    timeHorizons: { start: string; highlight: string; end: string };
    keyDefinitions: {
       title: string;
       raf: { term: string; def: string };
       hedis: { term: string; def: string };
       toc: { term: string; def: string };
    };
    flowTitle: string;
    flowDesc1: string;
    flowDesc2: string;
    models: {
      shared: { title: string; desc: string; income: string; bonus: string; risk: string };
      capitation: { title: string; desc: string; income: string; bonus: string; risk: string };
    };
    complianceTitle: string;
    complianceDesc: string;
    diagram: {
      cms: string;
      premium: string;
      plan: string;
      cap: string;
      mso: string;
      claims: string;
      practice: string;
      surplus: string;
      practiceSub: string;
    }
  };
  onboarding: {
    title: string;
    steps: { title: string; items: string[] }[];
  };
  resources: {
    title: string;
    subtitle: string;
    list: BlogPost[];
    cheatSheetTitle: string;
    cheatSheetDesc: string;
    downloadBtn: string;
  };
  contact: {
    title: string;
    subtitle: string;
    labels: {
      name: string;
      role: string;
      model: string;
      practice: string;
      email: string;
      patients: string;
      submit: string;
      privacy: string;
    };
    roles: string[];
    models: string[];
    directContact: string;
    scheduleDirectly: string;
    scanQr: string;
    calendar: { placeholder: string; desc: string };
    info: { hq: string; phone: string; email: string };
  };
  voiceAI: {
    title: string;
    initialGreeting: string;
    listening: string;
    tapToSpeak: string;
    processing: string;
    userSim: string;
    aiResponse1: string;
    aiResponse2: string;
  };
  legal: {
    title: string;
    sections: {
      terms: { title: string; content: string[] };
      privacy: { title: string; content: string[] };
      security: { title: string; content: string[] };
      cookies: { title: string; content: string[] };
      doNotShare: { title: string; content: string[] };
    }
  }
};

export const CONTENT: Record<Language, ContentStructure> = {
  en: {
    nav: NAV_ITEMS,
    common: {
      bookPerformanceCheck: "Book Performance Check",
      bookDemo: "Book a Demo",
      talkToAI: "Ask Suncoast AI",
      privacyNote: "Do not share PHI (Personal Health Information)",
      footerDesc: "Empowering independent physicians in Florida with the infrastructure to win in Medicare Advantage, without losing autonomy.",
      serving: "Serving Miami-Dade, Broward, Palm Beach, Lee, Collier, and Orange counties as well as all across Florida.",
      partnership: "Partnership",
      resources: "Resources",
      contact: "Contact",
      scheduleCall: "Schedule a Call",
      disclaimer: "* All financial examples are illustrative. Actual results vary based on patient panel, risk score, and utilization. All arrangements are subject to Fair Market Value (FMV) assessment and compliance with Stark Law and Anti-Kickback Statute.",
      rights: "Suncoast MD. All rights reserved.",
      links: {
        physicians: "For Physicians",
        economics: "Shared Savings Model",
        onboarding: "90-Day Roadmap",
        intake: "Partner Intake",
        realityCheck: "VBC Reality Check",
        blog: "Blog & Guides",
        hedis: "HEDIS Cheat Sheet",
        privacy: "Privacy Policy"
      },
      footerBottom: {
        terms: "Terms",
        privacy: "Privacy",
        security: "Security",
        cookies: "Manage Cookies",
        doNotShare: "Do Not Share My Personal Information"
      }
    },
    home: {
      heroTitle: "We Take the Load Off.",
      heroSubtitle: "You Keep the Earnings.",
      heroDesc: "A Florida PCP Medicare Advantage performance partner for shared savings and capitation—quality, RAF accuracy, and utilization control without burning out your clinic.",
      supporting: "Supporting PCPs in Miami-Dade, Broward, Palm Beach, Lee, Collier, and Orange counties as well as all across Florida.",
      earningsConcept: "Earn from Day One. Dive Deeper into the Surplus Pool Over Time.",
      economicsLabel: "Economics",
      timeHorizons: {
        p1: {
          start: "Our partnership is built on ",
          highlight: "two time horizons",
          end: ": immediate revenue stabilization and long-term wealth creation."
        },
        p2: {
          start: "Whether you want to stay in ",
          highlight1: "upside-only shared savings",
          mid: " or move to ",
          highlight2: "capitation",
          end: " for cash flow, our transparency engine ensures you know exactly where every dollar goes."
        }
      },
      onboardingPromo: {
        title: "Immediate Onboarding Revenue",
        desc: "Don't wait for shared savings. Get paid an onboarding fee for every new patient you enroll.",
        badge: "Earn from Day 1",
        note: "Paid per successful enrollment. Terms apply."
      },
      pillarsTitle: "The Three Pillars of MA Performance",
      pillarsSubtitle: "Where most practices leak revenue—and how we plug the gaps.",
      pillars: [
        { title: "Quality (Stars/HEDIS)", icon: "★", desc: "We deploy physical staff to close gaps, ensuring you hit 4+ Stars without your front desk chasing patients." },
        { title: "Risk (HCC/RAF)", icon: "⚡", desc: "Accurate, compliant coding support. We identify dropped conditions and help you recapture complexity annually." },
        { title: "Utilization (TOC)", icon: "📉", desc: "Real-time ADT feeds. We contact your patients within 24 hours of discharge to prevent readmissions." }
      ],
      twoLanesTitle: "Choose Your Lane. Keep Your Autonomy.",
      twoLanesDesc: "Unlike aggregators that want to buy your practice and change your sign, Suncoast MD is a performance partner.",
      lane1Title: "Lane 1: ACO / Shared Savings",
      lane1Desc: "Low risk. We provide the tools, you get the upside bonus.",
      lane2Title: "Lane 2: Capitation Partner",
      lane2Desc: "Higher reward. We take the downside risk and manage the claims. You get PMPM + surplus.",
      compareLink: "Compare models in detail →",
      proofTitle: "Built by Doctors, For Doctors",
      proofDesc: "We don't just advise; we practice. We use these same tools on our own patients every day.",
      credibilityLocations: "Owned clinics in Miami and Cape Coral + partner clinic in Fort Lauderdale",
      testimonialsTitle: "What Partners Say",
      timelineTitle: "Your First 90 Days",
      timelineSteps: [
         { day: "Day 30", title: "Data Integration", desc: "We connect to your EMR and establish payer data feeds. No heavy lifting for you." },
         { day: "Day 60", title: "Workflow Install", desc: "We deploy our TOC and gap-closure protocols. Your staff gets their time back." },
         { day: "Day 90", title: "First Performance Review", desc: "Review initial savings opportunities and coding accuracy improvements." }
      ],
      timelineLink: "See full onboarding roadmap →",
      whatChangesTitle: "What Changes Next Week?",
      whatChangesItems: [
        { title: "Pre-Visit Planning", desc: "We identify gaps & suspects before the patient walks in." },
        { title: "48-Hour TOC", desc: "We manage post-discharge calls to prevent readmissions." },
        { title: "Weekly Cadence", desc: "Routine check-ins on gap closure progress." },
        { title: "Annual Recapture", desc: "Structured discipline for HCC recapture." }
      ],
      faq: {
        title: "Frequently Asked Questions",
        items: [
          {
            question: "Do I have to sell my practice to work with Suncoast?",
            answer: "No. We are a performance partner, not an aggregator. You remain independent, keep your tax ID, and maintain full clinical autonomy. We simply provide the MSO infrastructure to help you succeed in value-based care."
          },
          {
            question: "What if I only have a few Medicare Advantage patients?",
            answer: "We work with practices of all sizes. For smaller panels, our Shared Savings model allows you to earn bonuses without taking on downside risk. As you grow, you can transition to higher-reward models."
          },
          {
            question: "How does the 'Two Lanes' model work?",
            answer: "Lane 1 is for practices that want low risk—you keep your FFS billing and we add a shared savings bonus layer. Lane 2 is for risk-ready practices looking for capitation, where you receive a monthly PMPM payment plus surplus distributions."
          },
          {
            question: "Does Suncoast integrate with my EMR?",
            answer: "Yes. We have established connections with most major EMRs (Athena, eClinicalWorks, etc.) to ingest data automatically. This allows us to handle HEDIS reporting and gap identification without burdening your staff."
          }
        ]
      },
      ctaTitle: "Ready to see what you're missing?",
      ctaDesc: "Take our 2-minute VBC Reality Check to see which model\nfits your practice best.",
      ctaButton: "Start VBC Reality Check",
      contactButton: "Contact Us"
    },
    earningsLadder: {
      title: "How Our Partnership Pays Off",
      steps: [
        { title: "Enroll", desc: "Enroll a new Medicare Advantage patient through our Multi-Carrier networks.", icon: "user-plus" },
        { title: "Onboard", desc: "Keep onboarding fees per patient seen during onboarding (Illustrative)", icon: "dollar-sign" },
        { title: "Scale", desc: "For every patient you enroll, you build toward surplus participation thresholds", icon: "chart-bar" }
      ],
      note: "Illustrative example only. Subject to contract terms."
    },
    calculator: {
      title: "Sample Pool Earnings",
      labels: {
        patients: "Patients",
        pool: "Pool Amount",
        surplus: "Surplus %",
        share: "Provider Share %"
      },
      results: {
        totalSurplus: "Total Surplus",
        providerShare: "Provider Share"
      },
      cta: "Book a 15–20 min Performance Check",
      disclaimer: "Illustrative projections only; subject to contract terms, quality gates, attribution rules, and compliance/FMV requirements.",
      accordionTitle: "How is this calculated?",
      accordionContent: "The surplus is derived from the Total Medical Expense (TME) savings against the benchmark. The Provider Share is a percentage of that surplus, distributed based on quality metrics and contract terms."
    },
    whyJoin: {
      title: "Why Join Suncoast MD",
      bullets: [
        "No upfront costs; fewer admin headaches",
        "Your patients and independent referrals remain yours",
        "Earn now + participate in surplus over time (subject to terms)",
        "Transparent scorecards and a clear cadence (weekly TOC, gap closure; monthly review)"
      ],
      faqTitle: "Common Questions",
      faqs: [
        { q: "Will I lose autonomy?", a: "No. You keep your practice, your staff, and your brand." },
        { q: "Is this capitation/downside risk?", a: "Only if you choose Lane 2. Lane 1 is upside-only shared savings." },
        { q: "How and when do distributions occur?", a: "Distributions typically occur annually for Shared Savings and quarterly for Capitation, subject to reconciliation." },
        { q: "What data do you need from us?", a: "We need a data feed from your EMR to track quality measures and admission data." }
      ]
    },
    curiousStrip: {
      title: "Curious to Learn More",
      emailBtn: "info@suncoastmdpartners.com",
      callBtn: "561-388-1805",
      bookBtn: "Book Performance Check",
      legal: "Providers receive percentage of surplus after enrolling patients. See sample table for projected earnings. 24/7 Access to a licensed Doctor."
    },
    testimonials: TESTIMONIALS,
    quiz: {
      ui: {
        title: "VBC Reality Check",
        intro: "Value-Based Care isn't one-size-fits-all. In 2 minutes, answer 10 questions to find out if you're in the right arrangement for your practice style and risk tolerance.",
        startBtn: "Start Diagnostic",
        meta: "Takes approx. 2 minutes • No PHI required",
        questionCount: "Question",
        yourArchetype: "YOUR ARCHETYPE",
        painPoints: "Likely Pain Points",
        fix: "The Suncoast Fix",
        bookCta: "Want to fix these pain points?",
        bookDesc: "Schedule a 15-minute diagnostic call with our physician leadership.",
        truthTableTitle: "Capitation Truth Table",
        truthTableDesc: "How your current model compares to Suncoast.",
        tableHeaders: ["Model", "Risk Profile", "Upside Potential", "Admin Burden"],
        tableRows: {
            ffs: ["Pure FFS", "None", "None", "High (Unpaid)"],
            shared: ["Shared Savings (Basic)", "Low", "Bonus Pool", "Medium"],
            suncoast: ["Suncoast Partner", "Managed / Reinsured", "Max Surplus + PMPM", "Low (We do it)"]
        }
      },
      questions: QUIZ_QUESTIONS,
      results: QUIZ_RESULTS
    },
    physicians: {
      title: "Partner with Suncoast MD",
      subtitle: "Focus on the patient. We handle the paperwork, data, and risk.",
      pcpTitle: "Independent PCPs",
      pcpPoints: ["Keep your brand and your office.", "Access to our care coordinators.", "Transparent savings distributions."],
      adminTitle: "Administrators",
      adminPoints: ["Automated HEDIS & gap reporting.", "Lower phone volume via our TOC team.", "Predictable revenue streams."],
      voicesSection: {
        title: "Physician Voices",
        subtitle: "Hear directly from your peers."
      }
    },
    economics: {
      title: "How the Money Works",
      subtitle: "Transparency is our core product. No black boxes. No hidden fees eroding your bonus.",
      timeHorizons: {
        start: "We see your success through ",
        highlight: "two time horizons",
        end: ": immediate revenue stabilization and long-term wealth creation."
      },
      keyDefinitions: {
        title: "Key Definitions:",
        raf: {
          term: "RAF (Risk Adjustment Factor):",
          def: " A score measuring patient complexity. Accurate coding ensures the budget matches the patient's needs."
        },
        hedis: {
          term: "HEDIS (Healthcare Effectiveness Data and Information Set):",
          def: " Quality metrics (like screenings) that determine your bonus eligibility."
        },
        toc: {
          term: "TOC (Transitions of Care):",
          def: " Managing patients after hospital discharge to prevent readmissions."
        }
      },
      flowTitle: "The Funds Flow",
      flowDesc1: "In a typical relationship, the MSO sits between the Health Plan and you. Many MSOs treat this as a 'black box', absorbing surplus in opaque fees.",
      flowDesc2: "The Suncoast Difference: We operate on a fixed management fee or transparent split. You see the raw data from the plan, the claims expenses, and the resulting surplus.",
      models: {
        shared: { title: "Shared Savings (\"Upside\" Model)", desc: "Best for practices starting in VBC.", income: "Standard FFS billing + Care Management PMPM.", bonus: "Paid annually if medical costs are below benchmark and Quality (Stars) is high.", risk: "None. If costs exceed benchmark, you simply don't receive a bonus." },
        capitation: { title: "Capitation (\"Partner\" Model)", desc: "Best for mature practices seeking cash flow.", income: "Monthly PMPM for primary services (replaces FFS) + Surplus.", bonus: "Quarterly distributions based on fund performance.", risk: "Suncoast absorbs catastrophic risk via reinsurance, protecting your solvency." }
      },
      complianceTitle: "Compliance & Fair Market Value",
      complianceDesc: "All models comply with Stark Law and Anti-Kickback Statute. Compensation is determined by FMV valuation. We do not pay for referrals; we pay for quality management and outcomes.",
      diagram: {
        cms: "CMS",
        premium: "Premium Payment",
        plan: "Health Plan",
        cap: "% Capitation / AC",
        mso: "MSO & Platform",
        claims: "Claims Processing & Reinsurance",
        practice: "Your Practice",
        surplus: "Surplus + Fees",
        practiceSub: "PMPM + Surplus"
      }
    },
    onboarding: {
      title: "90-Day Roadmap",
      steps: [
        { title: "Day 1-30: Discovery & Data", items: ["Review contracts and attribution lists.", "Establish EMR connectivity.", "Kick-off meeting with staff."] },
        { title: "Day 31-60: Workflow Integration", items: ["Deploy high-risk patient list.", "Initiate 48h post-discharge calls.", "HCC documentation training."] },
        { title: "Day 61-90: Performance Activation", items: ["First quarterly report delivered.", "Identify \"recapture\" opportunities.", "Gap closure for Stars."] }
      ]
    },
    resources: {
      title: "Resource Center",
      subtitle: "Guides, policy updates, and operational tips.",
      list: RESOURCES,
      cheatSheetTitle: "Need Help?",
      cheatSheetDesc: "Download our 2024 HEDIS Quick Reference Guide.",
      downloadBtn: "Download PDF"
    },
    contact: {
      title: "Book an Assessment",
      subtitle: "A 15-minute introductory call to see if our models fit your clinic.",
      labels: {
        name: "Full Name",
        role: "Role",
        model: "Current Model",
        practice: "Practice Name",
        email: "Email Address",
        patients: "Estimated MA Patients",
        submit: "Request Link",
        privacy: "By submitting, you agree to our privacy policy. Do not enter PHI here."
      },
      roles: ["Physician", "Manager", "Owner"],
      models: ["Unsure", "FFS Only", "Shared Savings", "Capitation"],
      directContact: "Direct Contact",
      scheduleDirectly: "Or Schedule Directly",
      scanQr: "Scan to Book",
      calendar: {
        placeholder: "Calendly Embed",
        desc: "Interactive calendar would load here."
      },
      info: {
        hq: "HQ",
        phone: "Phone",
        email: "Email"
      }
    },
    voiceAI: {
      title: "Suncoast MD Assistant",
      initialGreeting: "AI: Hi, I'm the Suncoast MD virtual assistant. I can help qualify your practice for our shared savings program. To start, how many Medicare Advantage patients do you currently see?",
      listening: "Listening...",
      tapToSpeak: "Tap to speak",
      processing: "Processing...",
      userSim: "You: (Simulated Voice Input) About 350 patients across 3 different payers.",
      aiResponse1: "AI: That's a great size for our 'Two Lanes' model. With 350 patients, you might be eligible for our infrastructure support right away. Would you like to schedule a 15-minute review with a director?",
      aiResponse2: "AI: Thanks for that info. Based on what you've shared, you fit our Shared Savings profile. A team member will be in touch."
    },
    legal: {
      title: "Legal & Compliance",
      sections: {
        terms: {
          title: "Terms of Service",
          content: [
            "Welcome to Suncoast MD Partners. By accessing our website, you agree to these Terms of Service.",
            "This website is intended for healthcare professionals in Florida. The content provided is for informational purposes regarding our MSO services.",
            "Disclaimer: The information on this site does not constitute medical, legal, or financial advice.",
            "Intellectual Property: All content, logos, and tools are property of Suncoast MD Partners."
          ]
        },
        privacy: {
          title: "Privacy Policy",
          content: [
            "Suncoast MD Partners respects your privacy. This policy outlines how we collect, use, and protect your information.",
            "Information Collection: We collect information you voluntarily provide via our contact forms.",
            "Usage: We use this information solely to contact you regarding our partnership opportunities.",
            "HIPAA: This website is not a channel for transmitting Protected Health Information (PHI)."
          ]
        },
        security: {
          title: "Security & Compliance",
          content: [
            "Mantenemos estrictos estándares de seguridad para proteger a nuestros socios y sus datos.",
            "Nuestros sistemas internos cumplen con HIPAA. Utilizamos canales encriptados para todo intercambio de datos.",
            "Este sitio web público utiliza encriptación SSL para proteger sus consultas."
          ]
        },
        cookies: {
          title: "Cookie Policy",
          content: [
            "Utilizamos cookies para mejorar la funcionalidad del sitio web y analizar patrones de tráfico.",
            "Cookies Esenciales: Requeridas para que el sitio funcione.",
            "Gestión: Puede controlar las preferencias de cookies a través de la configuración de su navegador."
          ]
        },
        doNotShare: {
          title: "Do Not Share My Personal Information",
          content: [
            "Suncoast MD Partners does not sell your personal information.",
            "If you are a resident of a jurisdiction with specific data privacy rights, you have the right to request we do not share your data.",
            "To exercise your rights, please email info@suncoastmdpartners.com."
          ]
        }
      }
    }
  },
  es: {
    nav: [
      { label: 'Inicio', path: '/' },
      { label: 'Para Médicos', path: '/physicians' },
      { label: 'Economía', path: '/economics' },
      { label: 'Diagnóstico VBC', path: '/reality-check' },
      { label: 'Incorporación', path: '/onboarding' },
      { label: 'Recursos', path: '/resources' },
    ],
    common: {
      bookPerformanceCheck: "Agendar Revisión de Rendimiento",
      bookDemo: "Agendar Demo",
      talkToAI: "Preguntar a IA Suncoast",
      privacyNote: "No comparta PHI (Información de Salud Protegida)",
      footerDesc: "Empoderando a médicos independientes en Florida con la infraestructura para ganar en Medicare Advantage, sin perder autonomía.",
      serving: "Sirviendo a los condados de Miami-Dade, Broward, Palm Beach, Lee, Collier y Orange, así como en todo el estado de Florida.",
      partnership: "Asociación",
      resources: "Recursos",
      contact: "Contacto",
      scheduleCall: "Programar Llamada",
      disclaimer: "* Todos los ejemplos financieros son ilustrativos. Los resultados reales varían según el panel de pacientes, puntuación de riesgo y utilización. Todos los acuerdos están sujetos a evaluación de Valor Justo de Mercado (FMV) y cumplimiento con la Ley Stark y Estatuto Anti-Soborno.",
      rights: "Suncoast MD. Todos los derechos reservados.",
      links: {
        physicians: "Para Médicos",
        economics: "Modelo de Ahorros Compartidos",
        onboarding: "Hoja de Ruta de 90 Días",
        intake: "Admisión de Socios",
        realityCheck: "Diagnóstico VBC",
        blog: "Blog y Guías",
        hedis: "Hoja de Trucos HEDIS",
        privacy: "Política de Privacidad"
      },
      footerBottom: {
        terms: "Términos",
        privacy: "Privacidad",
        security: "Seguridad",
        cookies: "Gestionar Cookies",
        doNotShare: "No Compartir Mi Información Personal"
      }
    },
    home: {
      heroTitle: "Aliviamos la Carga.",
      heroSubtitle: "Usted Conserva las Ganancias.",
      heroDesc: "Un socio de rendimiento de Medicare Advantage para PCP en Florida para ahorros compartidos y capitación: calidad, precisión RAF y control de utilización sin agotar su clínica.",
      supporting: "Apoyando a PCP en los condados de Miami-Dade, Broward, Palm Beach, Lee, Collier y Orange, así como en todo el estado de Florida.",
      earningsConcept: "Gane desde el Primer Día. Profundice en el Fondo de Excedentes con el Tiempo.",
      economicsLabel: "Economía",
      timeHorizons: {
        p1: {
          start: "Nuestra asociación se basa en ",
          highlight: "dos horizontes temporales",
          end: ": estabilización inmediata de ingresos y creación de riqueza a largo plazo."
        },
        p2: {
          start: "Ya sea que quiera quedarse en ",
          highlight1: "ahorros compartidos solo al alza",
          mid: " o pasar a ",
          highlight2: "capitación",
          end: " para flujo de caja, nuestro motor de transparencia asegura que sepa exactamente a dónde va cada dólar."
        }
      },
      onboardingPromo: {
        title: "Ingresos Inmediatos de Incorporación",
        desc: "No espere por los ahorros compartidos. Reciba una tarifa de incorporación por cada nuevo paciente que inscriba.",
        badge: "Gane desde el Día 1",
        note: "Pagado por inscripción exitosa. Aplican términos."
      },
      pillarsTitle: "Los Tres Pilares del Rendimiento MA",
      pillarsSubtitle: "Donde la mayoría de las prácticas pierden ingresos, y cómo cerramos las brechas.",
      pillars: [
        { title: "Calidad (Stars/HEDIS)", icon: "★", desc: "Desplegamos personal físico para cerrar brechas, asegurando que alcance 4+ Estrellas sin que su recepción persiga pacientes." },
        { title: "Riesgo (HCC/RAF)", icon: "⚡", desc: "Soporte de codificación preciso y conforme. Identificamos condiciones omitidas y le ayudamos a recapturar complejidad anualmente." },
        { title: "Utilización (TOC)", icon: "📉", desc: "Fuentes ADT en tiempo real. Contactamos a sus pacientes dentro de las 24 horas del alta para prevenir reingresos." }
      ],
      twoLanesTitle: "Elija Su Carril. Mantenga Su Autonomía.",
      twoLanesDesc: "A diferencia de los agregadores que quieren comprar su práctica y cambiar su letrero, Suncoast MD es un socio de rendimiento.",
      lane1Title: "Carril 1: ACO / Ahorros Compartidos",
      lane1Desc: "Bajo riesgo. Proporcionamos las herramientas, usted obtiene el bono al alza.",
      lane2Title: "Carril 2: Socio de Capitación",
      lane2Desc: "Mayor recompensa. Asumimos el riesgo a la baja y gestionamos las reclamaciones. Usted recibe PMPM + excedente.",
      compareLink: "Comparar modelos en detalle →",
      proofTitle: "Creado por Doctores, Para Doctores",
      proofDesc: "No solo asesoramos; practicamos. Usamos estas mismas herramientas en nuestros propios pacientes todos los días.",
      credibilityLocations: "Clínicas propias en Miami y Cape Coral + clínica asociada en Fort Lauderdale",
      testimonialsTitle: "Lo Que Dicen los Socios",
      timelineTitle: "Sus Primeros 90 Días",
      timelineSteps: [
         { day: "Día 30", title: "Integración de Datos", desc: "Nos conectamos a su EMR y establecemos fuentes de datos de pagadores. Sin trabajo pesado para usted." },
         { day: "Día 60", title: "Instalación de Flujo", desc: "Desplegamos nuestros protocolos TOC y cierre de brechas. Su personal recupera su tiempo." },
         { day: "Día 90", title: "Primera Revisión", desc: "Revisar oportunidades iniciales de ahorro y mejoras en precisión de codificación." }
      ],
      timelineLink: "Ver hoja de ruta completa →",
      whatChangesTitle: "¿Qué Cambia la Próxima Semana?",
      whatChangesItems: [
        { title: "Planificación Pre-Visita", desc: "Identificamos brechas y sospechas antes de que el paciente entre." },
        { title: "TOC de 48 Horas", desc: "Gestionamos llamadas post-alta para prevenir reingresos." },
        { title: "Cadencia Semanal", desc: "Revisiones rutinarias sobre el progreso del cierre de brechas." },
        { title: "Recaptura Anual", desc: "Disciplina estructurada para recaptura de HCC." }
      ],
      faq: {
        title: "Preguntas Frecuentes",
        items: [
          {
            question: "¿Tengo que vender mi práctica para trabajar con Suncoast?",
            answer: "No. Somos un socio de rendimiento, no un agregador. Usted permanece independiente, mantiene su ID fiscal y autonomía clínica completa. Simplemente proporcionamos la infraestructura MSO para ayudarlo a tener éxito en la atención basada en el valor."
          },
          {
            question: "¿Qué pasa si solo tengo unos pocos pacientes de Medicare Advantage?",
            answer: "No. Trabajamos con prácticas de todos los tamaños. Para paneles más pequeños, nuestro modelo de Ahorros Compartidos le permite ganar bonos sin asumir riesgo a la baja. A medida que crece, puede hacer la transición a modelos de mayor recompensa."
          },
          {
            question: "¿Cómo funciona el modelo de 'Dos Carriles'?",
            answer: "El Carril 1 es para prácticas que desean bajo riesgo: mantiene su facturación FFS y agregamos una capa de bono de ahorros compartidos. El Carril 2 es para prácticas listas para el riesgo que buscan capitación, donde recibe un pago mensual PMPM más distribuciones de excedentes."
          },
          {
            question: "¿Suncoast se integra con mi EMR?",
            answer: "Sí. Tenemos conexiones establecidas con la mayoría de los principales EMR (Athena, eClinicalWorks, etc.) para ingerir datos automáticamente. Esto nos permite manejar informes HEDIS e identificación de brechas sin cargar a su personal."
          }
        ]
      },
      ctaTitle: "¿Listo para ver lo que se está perdiendo?",
      ctaDesc: "Tome nuestra verificación de realidad VBC de 2 minutos para ver qué modelo\nse ajusta mejor a su práctica.",
      ctaButton: "Iniciar Diagnóstico VBC",
      contactButton: "Contáctenos"
    },
    earningsLadder: {
      title: "Cómo Rinde Nuestra Asociación",
      steps: [
        { title: "Inscribir", desc: "Inscriba un nuevo paciente de Medicare Advantage a través de nuestras redes Multi-Carrier.", icon: "user-plus" },
        { title: "Incorporar", desc: "Mantenga tarifas de incorporación por paciente visto durante la incorporación (Ilustrativo)", icon: "dollar-sign" },
        { title: "Escalar", desc: "Por cada paciente que inscribe, avanza hacia umbrales de participación en excedentes", icon: "chart-bar" }
      ],
      note: "Ejemplo ilustrativo solamente. Sujeto a términos del contrato."
    },
    calculator: {
      title: "Ganancias de Fondo de Muestra",
      labels: {
        patients: "Pacientes",
        pool: "Monto del Fondo",
        surplus: "Excedente Total",
        share: "% Parte del Proveedor"
      },
      results: {
        totalSurplus: "Excedente Total",
        providerShare: "Parte del Proveedor"
      },
      cta: "Agendar una Revisión de 15-20 min",
      disclaimer: "Proyecciones ilustrativas solamente; sujeto a términos del contrato, puertas de calidad, reglas de atribución y requisitos de cumplimiento/FMV.",
      accordionTitle: "¿Cómo se calcula esto?",
      accordionContent: "El excedente se deriva de los ahorros en Gastos Médicos Totales (TME) frente al punto de referencia. La Parte del Proveedor es un porcentaje de ese excedente, distribuido según métricas de calidad y términos del contrato."
    },
    whyJoin: {
      title: "Por Qué Unirse a Suncoast MD",
      bullets: [
        "Sin costos iniciales; menos dolores de cabeza administrativos",
        "Sus pacientes y referencias independientes siguen siendo suyos",
        "Gane ahora + participe en excedentes con el tiempo (sujeto a términos)",
        "Tarjetas de puntuación transparentes y una cadencia clara (TOC semanal, cierre de brechas; revisión mensual)"
      ],
      faqTitle: "Preguntas Comunes",
      faqs: [
        { q: "¿Perderé autonomía?", a: "No. Usted mantiene su práctica, su personal y su marca." },
        { q: "¿Es esto capitación/riesgo a la baja?", a: "Solo si elige el Carril 2. El Carril 1 es ahorros compartidos solo al alza." },
        { q: "¿Cómo y cuándo ocurren las distribuciones?", a: "Las distribuciones generalmente ocurren anualmente para Ahorros Compartidos y trimestralmente para Capitación, sujeto a conciliación." },
        { q: "¿Qué datos necesitan de nosotros?", a: "Necesitamos una fuente de datos de su EMR para rastrear medidas de calidad y datos de admisión." }
      ]
    },
    curiousStrip: {
      title: "Curioso por Saber Más",
      emailBtn: "info@suncoastmdpartners.com",
      callBtn: "561-388-1805",
      bookBtn: "Agendar Revisión",
      legal: "Los proveedores reciben porcentaje del excedente tras inscribir pacientes. Ver tabla de muestra. Acceso 24/7 a un médico licenciado."
    },
    testimonials: TESTIMONIALS_ES,
    quiz: {
      ui: {
        title: "Diagnóstico VBC",
        intro: "La Atención Basada en el Valor no es talla única. En 2 minutos, responda 10 preguntas para saber si está en el acuerdo correcto para su estilo de práctica y tolerancia al riesgo.",
        startBtn: "Iniciar Diagnóstico",
        meta: "Toma aprox. 2 minutos • No requiere PHI",
        questionCount: "Pregunta",
        yourArchetype: "SU ARQUETIPO",
        painPoints: "Puntos de Dolor Probables",
        fix: "La Solución Suncoast",
        bookCta: "¿Quiere solucionar estos puntos?",
        bookDesc: "Programe una llamada de diagnóstico de 15 minutos con nuestro liderazgo médico.",
        truthTableTitle: "Tabla de Verdad de Capitación",
        truthTableDesc: "Cómo se compara su modelo actual con Suncoast.",
        tableHeaders: ["Modelo", "Perfil de Riesgo", "Potencial Al Alza", "Carga Admin"],
        tableRows: {
            ffs: ["FFS Puro", "Ninguno", "Ninguno", "Alta (No Pagada)"],
            shared: ["Ahorros Compartidos", "Bajo", "Fondo de Bono", "Media"],
            suncoast: ["Socio Suncoast", "Gestionado / Reasegurado", "Excedente Máx + PMPM", "Baja (Lo hacemos)"]
        }
      },
      questions: QUIZ_QUESTIONS, 
      results: QUIZ_RESULTS
    },
    physicians: {
      title: "Asóciese con Suncoast MD",
      subtitle: "Enfóquese en el paciente. Nosotros manejamos el papeleo, los datos y el riesgo.",
      pcpTitle: "PCP Independientes",
      pcpPoints: ["Mantenga su marca y su oficina.", "Acceso a nuestros coordinadores de atención.", "Distribuciones de ahorros transparentes."],
      adminTitle: "Administradores",
      adminPoints: ["Informes HEDIS y de brechas automatizados.", "Menor volumen de llamadas vía nuestro equipo TOC.", "Flujos de ingresos predecibles."],
      voicesSection: {
        title: "Voces de Médicos",
        subtitle: "Escuche directamente de sus colegas."
      }
    },
    economics: {
      title: "Cómo Funciona el Dinero",
      subtitle: "La transparencia es nuestro producto principal. Sin cajas negras. Sin tarifas ocultas erosionando su bono.",
      timeHorizons: {
        start: "Vemos su éxito a través de ",
        highlight: "dos horizontes temporales",
        end: ": estabilización inmediata de ingresos y creación de riqueza a largo plazo."
      },
      keyDefinitions: {
        title: "Definiciones Clave:",
        raf: {
          term: "RAF (Factor de Ajuste de Riesgo):",
          def: " Una puntuación que mide la complejidad del paciente. La codificación precisa asegura que el presupuesto coincida con las necesidades del paciente."
        },
        hedis: {
          term: "HEDIS (Conjunto de Datos e Información de Efectividad en Salud):",
          def: " Métricas de calidad (como exámenes) que determinan su elegibilidad para bonos."
        },
        toc: {
          term: "TOC (Transiciones de Atención):",
          def: " Gestión de pacientes después del alta hospitalaria para prevenir reingresos."
        }
      },
      flowTitle: "El Flujo de Fondos",
      flowDesc1: "En una relación típica, la MSO se sienta entre el Plan de Salud y usted. Muchas MSO tratan esto como una 'caja negra', absorbiendo excedentes en tarifas opacas.",
      flowDesc2: "La Diferencia Suncoast: Operamos con una tarifa de gestión fija o división transparente. Usted ve los datos brutos del plan, los gastos de reclamaciones y el excedente resultante.",
      models: {
        shared: { title: "Ahorros Compartidos (Modelo \"Al Alza\")", desc: "Mejor para prácticas que inician en VBC.", income: "Facturación FFS estándar + PMPM de Gestión de Atención.", bonus: "Pagado anualmente si los costos médicos están por debajo del punto de referencia y la Calidad (Stars) es alta.", risk: "Ninguno. Si los costos exceden el punto de referencia, simplemente no recibe bono." },
        capitation: { title: "Capitación (Modelo \"Socio\")", desc: "Mejor para prácticas maduras que buscan flujo de caja.", income: "PMPM mensual para servicios primarios (reemplaza FFS) + Excedente.", bonus: "Distribuciones trimestrales basadas en el rendimiento del fondo.", risk: "Suncoast activo el riesgo catastrófico vía reaseguro, protegiendo su solvencia." }
      },
      complianceTitle: "Cumplimiento y Valor Justo de Mercado",
      complianceDesc: "Todos los modelos cumplen con la Ley Stark y el Estatuto Anti-Soborno. La compensación se determina por valoración FMV. No pagamos por referencias; pagamos por gestión de calidad y resultados.",
      diagram: {
        cms: "CMS",
        premium: "Pago de Prima",
        plan: "Plan de Salud",
        cap: "% Capitation / AC",
        mso: "MSO y Plataforma",
        claims: "Procesamiento de Reclamaciones y Reaseguro",
        practice: "Su Práctica",
        surplus: "Excedente + Tarifas",
        practiceSub: "PMPM + Excedente"
      }
    },
    onboarding: {
      title: "Hoja de Ruta de 90 Días",
      steps: [
        { title: "Días 1-30: Descubrimiento y Datos", items: ["Revisar contratos y listas de atribución.", "Establecer conectividad EMR.", "Reunión de inicio con el personal."] },
        { title: "Días 31-60: Integración de Flujo de Trabajo", items: ["Desplegar lista de pacientes de alto riesgo.", "Iniciar llamadas de 48h post-alta.", "Capacitación en documentación HCC."] },
        { title: "Días 61-90: Activación de Rendimiento", items: ["Primer informe trimestral entregado.", "Identificar oportunidades de \"recaptura\".", "Cierre de brechas para Stars."] }
      ]
    },
    resources: {
      title: "Centro de Recursos",
      subtitle: "Guías, actualizaciones de políticas y consejos operativos.",
      list: RESOURCES_ES,
      cheatSheetTitle: "¿Necesita Ayuda?",
      cheatSheetDesc: "Descargue nuestra Guía de Referencia Rápida HEDIS 2024.",
      downloadBtn: "Descargar PDF"
    },
    contact: {
      title: "Reservar una Evaluación",
      subtitle: "Una llamada introductoria de 15 minutos para ver si nuestros modelos se ajustan a su clínica.",
      labels: {
        name: "Nombre Completo",
        role: "Rol",
        model: "Modelo Actual",
        practice: "Nombre de la Práctica",
        email: "Correo Electrónico",
        patients: "Pacientes MA Estimados",
        submit: "Solicitar Enlace",
        privacy: "Al enviar, acepta nuestra política de privacidad. No ingrese PHI aquí."
      },
      roles: ["Médico", "Gerente", "Dueño"],
      models: ["No Seguro", "Solo FFS", "Ahorros", "Capitación"],
      directContact: "Contacto Directo",
      scheduleDirectly: "O Agende Directo",
      scanQr: "Escanear para Reservar",
      calendar: {
        placeholder: "Calendly Insertado",
        desc: "El calendario interactivo se cargaría aquí."
      },
      info: {
        hq: "Sede",
        phone: "Teléfono",
        email: "Correo"
      }
    },
    voiceAI: {
      title: "Asistente Suncoast MD",
      initialGreeting: "IA: Hola, soy el asistente virtual de Suncoast MD. Puedo ayudar a calificar su práctica para nuestro programa de ahorros compartidos. Para empezar, ¿cuántos pacientes de Medicare Advantage atiende actualmente?",
      listening: "Escuchando...",
      tapToSpeak: "Toque para hablar",
      processing: "Procesando...",
      userSim: "Usted: (Entrada de Voz Simulada) Unos 350 pacientes en 3 pagadores diferentes.",
      aiResponse1: "IA: Ese es un gran tamaño para nuestro modelo de 'Dos Carriles'. Con 350 pacientes, podría ser elegible para nuestro soporte de infraestructura de inmediato. ¿Le gustaría programar una revisión de 15 minutos con un director?",
      aiResponse2: "IA: Gracias por esa información. Basado en lo que ha compartido, encaja en nuestro perfil de Ahorros Compartidos. Un miembro del equipo se pondrá en contacto."
    },
    legal: {
      title: "Legal y Cumplimiento",
      sections: {
        terms: {
          title: "Términos de Servicio",
          content: [
            "Bienvenido a Suncoast MD Partners. Al acceder a nuestro sitio web, acepta estos Términos de Servicio.",
            "Este sitio web está destinado a profesionales de la salud en Florida. El contenido proporcionado es con fines informativos sobre nuestros servicios MSO.",
            "Descargo de responsabilidad: La información en este sitio no constituye asesoramiento médico, legal o financiero.",
            "Propiedad Intelectual: Todo el contenido, logotipos y herramientas son propiedad de Suncoast MD Partners."
          ]
        },
        privacy: {
          title: "Política de Privacidad",
          content: [
            "Suncoast MD Partners respecta su privacidad. Esta política describe cómo recopilamos, usamos y protegemos su información.",
            "Recopilación de Información: Recopilamos información que usted proporciona voluntariamente a través de nuestros formularios de contacto.",
            "Uso: Utilizamos esta información únicamente para contactarlo con respecto a nuestras oportunidades de asociación.",
            "HIPAA: Este sitio web no es un canal para transmitir Información de Salud Protegida (PHI)."
          ]
        },
        security: {
          title: "Seguridad y Cumplimiento",
          content: [
            "Mantenemos estrictos estándares de seguridad para proteger a nuestros socios y sus datos.",
            "Nuestros sistemas internos cumplen con HIPAA. Utilizamos canales encriptados para todo intercambio de datos.",
            "Este sitio web público utiliza encriptación SSL para proteger sus consultas."
          ]
        },
        cookies: {
          title: "Política de Cookies",
          content: [
            "Utilizamos cookies para mejorar la funcionalidad del sitio web y analizar patrones de tráfico.",
            "Cookies Esenciales: Requeridas para que el sitio funcione.",
            "Gestión: Puede controlar las preferencias de cookies a través de la configuración de su navegador."
          ]
        },
        doNotShare: {
          title: "No Compartir Mi Información Personal",
          content: [
            "Suncoast MD Partners no vende su información personal.",
            "Si usted es residente de una jurisdicción con derechos de privacidad de datos específicos, tiene derecho a solicitar que no compartamos sus datos.",
            "Para ejercer sus derechos, envíe un correo electrónico a info@suncoastmdpartners.com."
          ]
        }
      }
    }
  }
};
